// Canonical Insights context and exploration state. URL parameters survive
// drill-in links and browser back; workspace switches clear the old context.
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { defaultRange } from '@/lib/insightsRange';
import { useWorkspaceStore } from '@/stores/workspace';

const EXPLORER_KEYS = ['category_id', 'merchant_normalized', 'currency', 'valuation_status', 'recorded_by', 'day', 'group', 'tab', 'receipt', 'group_id'] as const;
export type InsightsFilterKey = typeof EXPLORER_KEYS[number];

export interface InsightsRange {
  fromParam: string;
  toParam: string;
  userIdParam: string;
  categoryId: string;
  merchant: string;
  originalCurrency: string;
  valuationStatus: string;
  recordedBy: string;
  day: string;
  group: 'category' | 'merchant' | 'day';
  tab: 'overview' | 'details' | 'reconcile';
  receiptId: string;
  setFilter: (key: InsightsFilterKey, value: string) => void;
  clearExplorerFilters: () => void;
  /** API filters, excluding presentation-only URL state. */
  explorerSearch: string;
  /** Build a path with the active range carried via search params. */
  link: (pathname: string) => string;
  /** Same params as a URLSearchParams string (no leading ?). */
  search: string;
}

export function useInsightsRange(): InsightsRange {
  const [params, setParams] = useSearchParams();
  const workspaceId = useWorkspaceStore((s) => s.current?.id);
  const previousWorkspace = useRef(workspaceId);
  useEffect(() => {
    if (previousWorkspace.current && workspaceId && previousWorkspace.current !== workspaceId) {
      setParams({}, { replace: true });
    }
    previousWorkspace.current = workspaceId;
  }, [workspaceId, setParams]);
  const initial = defaultRange();
  const fromParam = params.get('from') ?? initial.from;
  const toParam = params.get('to') ?? initial.to;
  const userIdParam = params.get('user_id') ?? '';

  const qs = new URLSearchParams();
  qs.set('from', fromParam);
  qs.set('to', toParam);
  if (userIdParam) qs.set('user_id', userIdParam);
  for (const key of EXPLORER_KEYS) {
    const value = params.get(key);
    if (value) qs.set(key, value);
  }
  const search = qs.toString();
  const day = params.get('day') ?? '';
  const explorerQs = new URLSearchParams({ from: day || fromParam, to: day || toParam });
  if (userIdParam) explorerQs.set('user_id', userIdParam);
  for (const key of ['category_id', 'merchant_normalized', 'currency', 'valuation_status', 'recorded_by']) {
    const value = params.get(key);
    if (value) explorerQs.set(key, value);
  }
  const setFilter = (key: InsightsFilterKey, value: string) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (!next.has('group_id') && !next.has('from')) next.set('from', fromParam);
      if (!next.has('group_id') && !next.has('to')) next.set('to', toParam);
      if (value) next.set(key, value);
      else next.delete(key);
      // Read the latest URL so rapid consecutive selections cannot restore
      // a previous tab or drop the category selected one interaction earlier.
      if (key !== 'receipt') next.delete('receipt');
      return next;
    });
  };

  return {
    fromParam,
    toParam,
    userIdParam,
    categoryId: params.get('category_id') ?? '',
    merchant: params.get('merchant_normalized') ?? '',
    originalCurrency: params.get('currency') ?? '',
    valuationStatus: params.get('valuation_status') ?? '',
    recordedBy: params.get('recorded_by') ?? '',
    day,
    group: params.get('group') === 'merchant' ? 'merchant' : params.get('group') === 'day' ? 'day' : 'category',
    tab: params.get('tab') === 'reconcile' ? 'reconcile' : params.get('tab') === 'details' ? 'details' : 'overview',
    receiptId: params.get('receipt') ?? '',
    setFilter,
    clearExplorerFilters: () => {
      const next = new URLSearchParams(search);
      for (const key of ['category_id', 'merchant_normalized', 'currency', 'valuation_status', 'recorded_by', 'day', 'receipt']) next.delete(key);
      setParams(next);
    },
    explorerSearch: explorerQs.toString(),
    search,
    link: (pathname: string) => `${pathname}?${search}`,
  };
}
